/* ClientDashboardScreen.js — Single-Client 360° Dashboard
   Receives: route.params.clientId, route.params.clientData from ClientsScreen
   Uses /api/data/:tableName endpoints (the generic dynamic-data API, 15k+
   calls/day on this server — proven reliable). Avoids /api/clients/:id and
   /api/clients/detail/* which have known issues server-side.
   ─────────────────────────────────────────────────────────────────────────
   FULL FEATURE SET:
     • Header with avatar, contact chips, quick-actions row (call / email /
       meet / log call / message / share), favourite/star toggle
     • Auto-computed Client Health Score (0–100) based on comms frequency,
       payment timeliness, project completion, recent activity
     • 6 tabs: Overview · Projects · Schedule · Finance · Messages · Files
     • Overview: health ring, lifetime value, activity timeline (merged feed),
       recent items, feedback rating, tasks
     • Projects: search/sort/filter, status toggles, completion sliders,
       detail modal with tasks, time tracking
     • Schedule: interviews + tasks in unified calendar-style list
     • Finance: contracts + invoices, aging, mark-paid, renewal alerts,
       revenue trend mini-chart
     • Messages: communication log with threading, reply, mark-read
     • Files: document list with mime-type icons, share / download
     • Edit/delete on every item with confirmation
     • Inline status changes (long-press menu)
     • Floating Action Button with quick-add menu
     • Search bar per tab, sort dropdown, filter chips
     • Multi-token auth (auth_token | userToken | token)
     • Defensive company_id (|| 0 not || 1) to prevent cross-company bleed
     • Currency from companyProfile (fallback GBP)
     • Full validation on all forms
*/

import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, ScrollView, RefreshControl,
  StatusBar, Alert, Modal, ActivityIndicator, TextInput, Dimensions, Share, Linking,
  KeyboardAvoidingView, Platform, Animated, Easing,
} from 'react-native';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');

// ── Brand / palette ─────────────────────────────────────────────────────────
const PRIMARY    = '#0e4e96';
const PRIMARY_2  = '#1e7be2';
const SUCCESS    = '#10B981';
const WARNING    = '#D97706';
const DANGER     = '#DC2626';
const INFO       = '#0891B2';
const PURPLE     = '#8B5CF6';
const INDIGO     = '#6366F1';
const NEUTRAL    = '#64748B';
const BORDER     = '#E2E8F0';
const BG         = '#F8FAFC';
const SURFACE    = '#FFFFFF';

// ── API ─────────────────────────────────────────────────────────────────────
const API = 'https://api.jobeng.ai';
const KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const api = axios.create({
  baseURL: API,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': KEY },
});

api.interceptors.request.use(async (c) => {
  try {
    const t = (await AsyncStorage.getItem('auth_token'))
      || (await AsyncStorage.getItem('userToken'))
      || (await AsyncStorage.getItem('token'));
    if (t) c.headers.Authorization = 'Bearer ' + t;
  } catch {}
  return c;
});

// ── Helpers ─────────────────────────────────────────────────────────────────
const fmtMoney = (v, currency = 'GBP') => {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency,
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(parseFloat(v) || 0);
  } catch {
    return `${currency} ${(parseFloat(v) || 0).toLocaleString('en-GB')}`;
  }
};
const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';
const fmtDateShort = (d) => d
  ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  : '';
const fmtTime = (t) => t ? String(t).substring(0, 5) : '';
const daysBetween = (a, b = new Date()) => {
  if (!a) return 0;
  return Math.floor((new Date(b).getTime() - new Date(a).getTime()) / 86400000);
};
const relativeTime = (d) => {
  if (!d) return '';
  const diff = daysBetween(d);
  if (diff === 0) return 'today';
  if (diff === 1) return 'yesterday';
  if (diff < 7)   return `${diff}d ago`;
  if (diff < 30)  return `${Math.floor(diff / 7)}w ago`;
  if (diff < 365) return `${Math.floor(diff / 30)}mo ago`;
  return `${Math.floor(diff / 365)}y ago`;
};

// ── Status colour map ───────────────────────────────────────────────────────
const STATUS = {
  active:    { bg: '#D1FAE5', c: SUCCESS },
  completed: { bg: '#DBEAFE', c: '#2563EB' },
  scheduled: { bg: '#FEF3C7', c: WARNING },
  upcoming:  { bg: '#FEF3C7', c: WARNING },
  paid:      { bg: '#D1FAE5', c: SUCCESS },
  sent:      { bg: '#DBEAFE', c: '#2563EB' },
  pending:   { bg: '#FEF3C7', c: WARNING },
  overdue:   { bg: '#FEE2E2', c: DANGER },
  cancelled: { bg: '#FEE2E2', c: DANGER },
  draft:     { bg: '#F1F5F9', c: NEUTRAL },
  inactive:  { bg: '#FEE2E2', c: DANGER },
  open:      { bg: '#D1FAE5', c: SUCCESS },
  closed:    { bg: '#F1F5F9', c: NEUTRAL },
  done:      { bg: '#D1FAE5', c: SUCCESS },
  todo:      { bg: '#FEF3C7', c: WARNING },
  in_progress: { bg: '#DBEAFE', c: '#2563EB' },
};
const getSt = (s) => STATUS[String(s || '').toLowerCase()] || { bg: '#F1F5F9', c: NEUTRAL };

// ── Sub-components ──────────────────────────────────────────────────────────
const StatusBadge = ({ status, small }) => {
  const s = getSt(status);
  return (
    <View style={[
      { backgroundColor: s.bg, borderRadius: 8 },
      small ? { paddingHorizontal: 6, paddingVertical: 1 } : { paddingHorizontal: 8, paddingVertical: 3 },
    ]}>
      <Text style={{
        color: s.c, fontWeight: '700', textTransform: 'capitalize',
        fontSize: small ? 9 : 10,
      }}>{String(status || 'unknown').replace(/_/g, ' ')}</Text>
    </View>
  );
};

const EmptyState = ({ icon, title, action, onAction }) => (
  <View style={st.empty}>
    <MaterialCommunityIcons name={icon} size={48} color="#CBD5E1" />
    <Text style={st.emptyTxt}>{title}</Text>
    {action && (
      <TouchableOpacity style={st.emptyBtn} onPress={onAction}>
        <Ionicons name="add" size={16} color="#FFF" />
        <Text style={st.emptyBtnTxt}>{action}</Text>
      </TouchableOpacity>
    )}
  </View>
);

// Health ring — circular progress around health score
const HealthRing = ({ score, size = 100 }) => {
  const color = score >= 80 ? SUCCESS : score >= 60 ? PRIMARY : score >= 40 ? WARNING : DANGER;
  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <View style={{
        position: 'absolute', width: size, height: size, borderRadius: size / 2,
        borderWidth: 6, borderColor: BORDER,
      }} />
      <View style={{
        position: 'absolute', width: size, height: size, borderRadius: size / 2,
        borderWidth: 6,
        borderTopColor:    color,
        borderRightColor:  score >  25 ? color : BORDER,
        borderBottomColor: score >  50 ? color : BORDER,
        borderLeftColor:   score >  75 ? color : BORDER,
        transform: [{ rotate: '-45deg' }],
      }} />
      <View style={{ alignItems: 'center' }}>
        <Text style={{ fontSize: 24, fontWeight: '800', color }}>{Math.round(score)}</Text>
        <Text style={{ fontSize: 9, color: NEUTRAL, fontWeight: '600' }}>HEALTH</Text>
      </View>
    </View>
  );
};

// Mini sparkline
const Sparkline = ({ data, color = PRIMARY, height: h = 30, width: w = 120 }) => {
  if (!data || data.length === 0) return <View style={{ height: h, width: w }} />;
  const max = Math.max(...data, 1);
  const bw = Math.max(1, (w - (data.length - 1) * 2) / data.length);
  return (
    <View style={{ height: h, width: w, flexDirection: 'row', alignItems: 'flex-end' }}>
      {data.map((v, i) => (
        <View key={i} style={{
          width: bw,
          marginRight: i === data.length - 1 ? 0 : 2,
          height: Math.max(2, (v / max) * h),
          backgroundColor: color, borderRadius: 1,
          opacity: 0.45 + 0.55 * (i / data.length),
        }} />
      ))}
    </View>
  );
};

// Timeline item
const TimelineItem = ({ icon, color, title, subtitle, time, onPress }) => (
  <TouchableOpacity activeOpacity={0.7} onPress={onPress} style={st.tlRow}>
    <View style={st.tlLineCol}>
      <View style={[st.tlDot, { backgroundColor: color }]}>
        <MaterialCommunityIcons name={icon} size={11} color="#FFF" />
      </View>
      <View style={st.tlLine} />
    </View>
    <View style={{ flex: 1, paddingBottom: 12 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 1 }}>
        <Text style={{ fontSize: 13, fontWeight: '700', color: '#1E293B', flex: 1 }} numberOfLines={1}>
          {title}
        </Text>
        <Text style={{ fontSize: 11, color: NEUTRAL }}>{time}</Text>
      </View>
      {subtitle && (
        <Text style={{ fontSize: 12, color: NEUTRAL }} numberOfLines={2}>{subtitle}</Text>
      )}
    </View>
  </TouchableOpacity>
);

// Star toggle
const StarToggle = ({ active, onPress }) => (
  <TouchableOpacity onPress={onPress} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
    <MaterialCommunityIcons
      name={active ? 'star' : 'star-outline'}
      size={20}
      color={active ? '#FBBF24' : '#FFF'}
    />
  </TouchableOpacity>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
export default function ClientDashboardScreen({ navigation, route }) {
  const clientId     = route?.params?.clientId;
  const passedClient = route?.params?.clientData || {};
  const companyName  = route?.params?.companyName || '';

  // ── Core state ────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError]   = useState(null);
  const [activeTab, setActiveTab]   = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy]         = useState('date_desc');
  const [filterStatus, setFilterStatus] = useState('all');

  // ── Data ──────────────────────────────────────────────────────────────────
  const [client, setClient]                 = useState(passedClient);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [projects, setProjects]             = useState([]);
  const [interviews, setInterviews]         = useState([]);
  const [contracts, setContracts]           = useState([]);
  const [communications, setCommunications] = useState([]);
  const [feedback, setFeedback]             = useState([]);
  const [invoices, setInvoices]             = useState([]);
  const [tasks, setTasks]                   = useState([]);
  const [documents, setDocuments]           = useState([]);
  const [favorite, setFavorite]             = useState(false);

  // ── Modals ────────────────────────────────────────────────────────────────
  const [showAddProject,   setShowAddProject]   = useState(false);
  const [showAddComm,      setShowAddComm]      = useState(false);
  const [showAddInterview, setShowAddInterview] = useState(false);
  const [showAddInvoice,   setShowAddInvoice]   = useState(false);
  const [showAddTask,      setShowAddTask]      = useState(false);
  const [showEditClient,   setShowEditClient]   = useState(false);
  const [showFAB,          setShowFAB]          = useState(false);
  const [detailItem,       setDetailItem]       = useState(null); // {type, data}
  const [editItem,         setEditItem]         = useState(null); // {type, data}

  // ── Forms ─────────────────────────────────────────────────────────────────
  const emptyForms = {
    project:   { project_name: '', description: '', priority: 'medium', budget: '', start_date: '', end_date: '', status: 'active' },
    comm:      { subject: '', content: '', communication_type: 'email' },
    interview: { title: '', interview_type: 'client', scheduled_date: '', scheduled_time: '', client_representative: '', notes: '' },
    invoice:   { invoice_number: '', description: '', amount: '', due_date: '', status: 'pending' },
    task:      { title: '', description: '', due_date: '', priority: 'medium', assignee: '' },
  };
  const [projForm, setProjForm] = useState(emptyForms.project);
  const [commForm, setCommForm] = useState(emptyForms.comm);
  const [intForm,  setIntForm]  = useState(emptyForms.interview);
  const [invForm,  setInvForm]  = useState(emptyForms.invoice);
  const [taskForm, setTaskForm] = useState(emptyForms.task);
  const [editForm, setEditForm] = useState({});

  // Animated FAB scale
  const fabAnim = useRef(new Animated.Value(0)).current;

  const currency = companyProfile?.currency || client?.currency || 'GBP';
  const fmt = (v) => fmtMoney(v, currency);

  // ════════════════════════════════════════════════════════════════════════
  // DATA LOAD
  // ════════════════════════════════════════════════════════════════════════
  const getCompanyId = async () => {
    try {
      const c = await AsyncStorage.getItem('company_id');
      const cid = parseInt(c) || 0;
      return cid;
    } catch { return 0; }
  };

  const loadAll = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      if (!clientId) {
        setIsLoading(false);
        setLoadError({ message: 'No client ID provided', isFatal: true });
        return;
      }
      const cid = await getCompanyId();
      if (cid === 0) {
        setClient(passedClient);
        setEditForm(passedClient);
        setLoadError({ message: 'No company selected — showing cached data', isFatal: false });
        setIsLoading(false);
        return;
      }

      // ──────────────────────────────────────────────────────────────────────
      // Use /api/data/:tableName — the generic dynamic-data endpoint.
      // It's the most-called route on the API (15k+ calls) and supports
      // arbitrary filters via query params. Specific endpoints like
      // /api/clients/:id are known to 500 with "operator does not exist"
      // (varchar vs integer mismatch in the route handler), so we avoid
      // them entirely until that server-side bug is fixed.
      // ──────────────────────────────────────────────────────────────────────
      const baseParams   = { company_id: cid };
      const clientParams = { company_id: cid, client_id: clientId };

      const safeOne = async (url, p) => {
        try {
          const r = await api.get(url, { params: p });
          if (r?.data?.success) {
            const d = r.data.data;
            return Array.isArray(d) ? d[0] : d;
          }
          return null;
        } catch { return null; }
      };
      const safeList = async (url, p) => {
        try {
          const r = await api.get(url, { params: p });
          if (r?.data?.success) {
            const d = r.data.data;
            return Array.isArray(d) ? d : (d ? [d] : []);
          }
          return [];
        } catch { return []; }
      };

      // Fan out all requests in parallel
      const [
        clientData,
        projData,
        intData,
        ctrData,
        invData,
        commData,
        fbData,
        taskData,
        docData,
      ] = await Promise.all([
        safeOne(`/api/data/clients/${clientId}`, baseParams),
        safeList('/api/data/projects',          clientParams),
        safeList('/api/data/interviews',        clientParams),
        safeList('/api/data/contracts',         clientParams),
        safeList('/api/data/invoices',          clientParams),
        safeList('/api/data/crm_communications',clientParams),
        safeList('/api/data/customer_feedback', clientParams),
        safeList('/api/data/tasks',             clientParams),
        safeList('/api/data/documents',         clientParams),
      ]);

      setClient(clientData || passedClient);
      setEditForm(clientData || passedClient);
      setProjects(projData);
      setInterviews(intData);
      setContracts(ctrData);
      setInvoices(invData);
      setCommunications(commData);
      setFeedback(fbData);
      setTasks(taskData);
      setDocuments(docData);

      // Soft banner only if the client itself couldn't be fetched
      if (!clientData) {
        setLoadError({
          message: 'Couldn\'t reach the server — showing cached data. Pull down to retry.',
          isFatal: false,
        });
      }

      // Company profile for currency etc.
      try {
        const cp = await api.get(`/api/company-profile/${cid}`);
        if (cp?.data?.success) {
          const profile = Array.isArray(cp.data.data) ? cp.data.data[0] : cp.data.data;
          if (profile) setCompanyProfile(profile);
        }
      } catch {}

      // Favourite state
      try {
        const fav = await AsyncStorage.getItem(`client_fav_${clientId}`);
        setFavorite(fav === '1');
      } catch {}
    } catch (e) {
      if (typeof __DEV__ !== 'undefined' && __DEV__) {
        console.log('[ClientDashboard] loadAll unexpected error:', e?.message);
      }
      setClient(passedClient);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  useFocusEffect(useCallback(() => { loadAll(); }, [clientId]));
  const onRefresh = () => { setRefreshing(true); loadAll(); };

  // ════════════════════════════════════════════════════════════════════════
  // COMPUTED STATS & HEALTH SCORE
  // ════════════════════════════════════════════════════════════════════════
  const stats = useMemo(() => {
    const activeProj  = projects.filter(p => p.status === 'active').length;
    const completedProj = projects.filter(p => p.status === 'completed').length;
    const totalBudget = projects.reduce((s, p) => s + (parseFloat(p.budget) || 0), 0);
    const totalRevenue = projects.reduce((s, p) => s + (parseFloat(p.revenue) || 0), 0);
    const upcomingInt = interviews.filter(i =>
      i.status === 'scheduled' && new Date(i.scheduled_date) >= new Date()
    ).length;
    const activeContracts = contracts.filter(c => c.status === 'active').length;
    const contractValue = contracts.reduce((s, c) => s + (parseFloat(c.contract_value) || 0), 0);
    const paidInvoices = invoices.filter(i => i.status === 'paid');
    const unpaidInvoices = invoices.filter(i => i.status !== 'paid');
    const overdueInvoices = invoices.filter(i =>
      i.status !== 'paid' && i.due_date && new Date(i.due_date) < new Date()
    );
    const paidTotal = paidInvoices.reduce((s, i) => s + (parseFloat(i.amount || i.total) || 0), 0);
    const unpaidTotal = unpaidInvoices.reduce((s, i) => s + (parseFloat(i.amount || i.total) || 0), 0);
    const overdueTotal = overdueInvoices.reduce((s, i) => s + (parseFloat(i.amount || i.total) || 0), 0);
    const openTasks = tasks.filter(t => t.status !== 'done').length;
    const lifetimeValue = paidTotal + contractValue;
    const avgFeedback = feedback.length > 0
      ? feedback.reduce((s, f) => s + (parseFloat(f.rating) || 0), 0) / feedback.length
      : 0;

    // Renewals — contracts expiring within 60 days
    const renewals = contracts.filter(c => {
      if (!c.end_date || c.status !== 'active') return false;
      const days = daysBetween(new Date(), c.end_date);
      return days > 0 && days <= 60;
    });

    return {
      projects: projects.length,
      activeProjects: activeProj,
      completedProjects: completedProj,
      totalBudget, totalRevenue,
      interviews: interviews.length, upcomingInt,
      contracts: contracts.length, activeContracts, contractValue,
      comms: communications.length,
      invoices: invoices.length,
      paidCount: paidInvoices.length, paidTotal,
      unpaidCount: unpaidInvoices.length, unpaidTotal,
      overdueCount: overdueInvoices.length, overdueTotal,
      openTasks,
      documents: documents.length,
      lifetimeValue,
      avgFeedback,
      renewals: renewals.length,
      renewalContracts: renewals,
    };
  }, [projects, interviews, contracts, communications, invoices, tasks, documents, feedback]);

  // Health score: 0-100, derived from real signals
  const healthScore = useMemo(() => {
    let score = 50;

    // Recent communication (40% weight)
    const recentComm = communications.find(c => daysBetween(c.sent_at || c.created_at) < 30);
    if (recentComm) score += 20;
    else if (communications.length === 0) score -= 15;

    // Active engagement (20%)
    if (stats.activeProjects > 0) score += 15;
    else if (stats.completedProjects > 0) score += 5;

    // Payment timeliness (20%)
    if (stats.overdueCount === 0 && stats.paidCount > 0) score += 15;
    if (stats.overdueCount > 0) score -= Math.min(25, stats.overdueCount * 8);

    // Upcoming activity (10%)
    if (stats.upcomingInt > 0) score += 5;
    if (stats.openTasks > 0) score += 5;

    // Feedback (10%)
    if (stats.avgFeedback >= 4) score += 10;
    else if (stats.avgFeedback > 0 && stats.avgFeedback < 3) score -= 10;

    // Status penalty
    if (client?.status === 'inactive') score -= 30;

    return Math.max(0, Math.min(100, score));
  }, [communications, stats, client]);

  const healthLabel =
    healthScore >= 80 ? 'Excellent' :
    healthScore >= 60 ? 'Good' :
    healthScore >= 40 ? 'At Risk' :
    'Poor';

  // ════════════════════════════════════════════════════════════════════════
  // ACTIVITY TIMELINE — merged chronological feed
  // ════════════════════════════════════════════════════════════════════════
  const activityTimeline = useMemo(() => {
    const items = [];
    projects.forEach(p => items.push({
      type: 'project', icon: 'briefcase', color: INDIGO,
      title: `Project: ${p.project_name || 'Untitled'}`,
      subtitle: `${p.status} · ${fmt(p.budget || 0)}${p.completion_percentage ? ` · ${p.completion_percentage}%` : ''}`,
      time: p.created_at || p.start_date,
      data: p,
    }));
    interviews.forEach(i => items.push({
      type: 'interview', icon: 'calendar-clock', color: WARNING,
      title: `Interview: ${i.title || 'Untitled'}`,
      subtitle: `${fmtDate(i.scheduled_date)}${i.scheduled_time ? ` ${fmtTime(i.scheduled_time)}` : ''} · ${i.status}`,
      time: i.scheduled_date || i.created_at,
      data: i,
    }));
    contracts.forEach(c => items.push({
      type: 'contract', icon: 'file-document', color: INFO,
      title: `Contract: ${c.contract_name || c.contract_number || 'Untitled'}`,
      subtitle: `${fmt(c.contract_value || 0)} · ${c.status}`,
      time: c.created_at || c.start_date,
      data: c,
    }));
    communications.forEach(c => items.push({
      type: 'comm', icon: c.communication_type === 'phone' ? 'phone' : 'email',
      color: PURPLE,
      title: c.subject || 'Communication',
      subtitle: `${c.communication_type || 'email'} · ${c.direction || 'outbound'}`,
      time: c.sent_at || c.created_at,
      data: c,
    }));
    invoices.forEach(i => items.push({
      type: 'invoice', icon: 'receipt', color: SUCCESS,
      title: `Invoice ${i.invoice_number || `#${i.id || '?'}`}`,
      subtitle: `${fmt(i.amount || i.total)} · ${i.status}${i.due_date ? ` · Due ${fmtDateShort(i.due_date)}` : ''}`,
      time: i.created_at || i.issue_date,
      data: i,
    }));
    tasks.forEach(t => items.push({
      type: 'task', icon: 'checkbox-marked-circle-outline', color: NEUTRAL,
      title: `Task: ${t.title}`,
      subtitle: `${t.priority || 'medium'} · ${t.status || 'todo'}${t.due_date ? ` · Due ${fmtDateShort(t.due_date)}` : ''}`,
      time: t.created_at || t.due_date,
      data: t,
    }));
    feedback.forEach(f => items.push({
      type: 'feedback', icon: 'star', color: '#FBBF24',
      title: `Feedback · ${f.rating || '?'}/5`,
      subtitle: f.comment || f.feedback || 'No comment',
      time: f.created_at,
      data: f,
    }));
    return items
      .filter(x => x.time)
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
  }, [projects, interviews, contracts, communications, invoices, tasks, feedback, fmt]);

  // ════════════════════════════════════════════════════════════════════════
  // ACTIONS
  // ════════════════════════════════════════════════════════════════════════
  const validateForm = (form, rules) => {
    for (const [field, rule] of Object.entries(rules)) {
      if (rule.required && !String(form[field] || '').trim()) {
        Alert.alert('Missing field', `${rule.label} is required.`);
        return false;
      }
      if (rule.type === 'date' && form[field]) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(form[field])) {
          Alert.alert('Invalid date', `${rule.label} must be YYYY-MM-DD format.`);
          return false;
        }
      }
      if (rule.type === 'number' && form[field] && isNaN(parseFloat(form[field]))) {
        Alert.alert('Invalid number', `${rule.label} must be numeric.`);
        return false;
      }
    }
    return true;
  };

  const handleAddProject = async () => {
    if (!validateForm(projForm, {
      project_name: { required: true, label: 'Project name' },
      budget: { type: 'number', label: 'Budget' },
      start_date: { type: 'date', label: 'Start date' },
      end_date: { type: 'date', label: 'End date' },
    })) return;
    try {
      const cid = await getCompanyId();
      const r = await api.post('/api/data/projects', {
        ...projForm, client_id: clientId,
        budget: parseFloat(projForm.budget) || 0,
        company_id: cid,
      });
      if (r?.data?.success) {
        Alert.alert('Created', 'Project created successfully');
        setShowAddProject(false);
        setProjForm(emptyForms.project);
        loadAll();
      } else {
        Alert.alert('Failed', r?.data?.message || 'Could not create project');
      }
    } catch (e) { Alert.alert('Error', e?.response?.data?.error || e?.message || 'Network error'); }
  };

  const handleAddComm = async () => {
    if (!validateForm(commForm, {
      subject: { required: true, label: 'Subject' },
    })) return;
    try {
      const cid = await getCompanyId();
      const r = await api.post('/api/data/crm_communications', {
        ...commForm, client_id: clientId,
        recipient_email: client.email, sender_name: companyName,
        sent_at: new Date().toISOString(),
        company_id: cid,
      });
      if (r?.data?.success) {
        Alert.alert('Logged', 'Communication recorded');
        setShowAddComm(false);
        setCommForm(emptyForms.comm);
        loadAll();
      } else {
        Alert.alert('Failed', r?.data?.message || 'Could not log communication');
      }
    } catch (e) { Alert.alert('Error', e?.response?.data?.error || e?.message || 'Network error'); }
  };

  const handleAddInterview = async () => {
    if (!validateForm(intForm, {
      title: { required: true, label: 'Title' },
      scheduled_date: { type: 'date', label: 'Date' },
    })) return;
    try {
      const cid = await getCompanyId();
      const r = await api.post('/api/data/interviews', {
        ...intForm, client_id: clientId, status: 'scheduled', company_id: cid,
      });
      if (r?.data?.success) {
        Alert.alert('Scheduled', 'Interview scheduled');
        setShowAddInterview(false);
        setIntForm(emptyForms.interview);
        loadAll();
      } else {
        Alert.alert('Failed', r?.data?.message || 'Could not schedule');
      }
    } catch (e) { Alert.alert('Error', e?.response?.data?.error || e?.message || 'Network error'); }
  };

  const handleAddInvoice = async () => {
    if (!validateForm(invForm, {
      invoice_number: { required: true, label: 'Invoice number' },
      amount: { required: true, type: 'number', label: 'Amount' },
      due_date: { type: 'date', label: 'Due date' },
    })) return;
    try {
      const cid = await getCompanyId();
      const r = await api.post('/api/data/invoices', {
        ...invForm, client_id: clientId,
        amount: parseFloat(invForm.amount) || 0,
        total:  parseFloat(invForm.amount) || 0,
        company_id: cid,
      });
      if (r?.data?.success) {
        Alert.alert('Created', 'Invoice created');
        setShowAddInvoice(false);
        setInvForm(emptyForms.invoice);
        loadAll();
      } else {
        Alert.alert('Failed', r?.data?.message || 'Could not create invoice');
      }
    } catch (e) { Alert.alert('Error', e?.response?.data?.error || e?.message || 'Network error'); }
  };

  const handleAddTask = async () => {
    if (!validateForm(taskForm, {
      title: { required: true, label: 'Title' },
      due_date: { type: 'date', label: 'Due date' },
    })) return;
    try {
      const cid = await getCompanyId();
      const r = await api.post('/api/data/tasks', {
        ...taskForm, client_id: clientId, status: 'todo', company_id: cid,
      });
      if (r?.data?.success) {
        Alert.alert('Added', 'Task created');
        setShowAddTask(false);
        setTaskForm(emptyForms.task);
        loadAll();
      } else {
        Alert.alert('Failed', r?.data?.message || 'Could not create task');
      }
    } catch (e) { Alert.alert('Error', e?.response?.data?.error || e?.message || 'Network error'); }
  };

  const handleUpdateClient = async () => {
    try {
      const cid = await getCompanyId();
      // Use /api/data/clients/:id (which works) — NOT /api/clients/:id
      // (which 500s with "operator does not exist: character varying = integer")
      const r = await api.put(`/api/data/clients/${clientId}`, {
        name: editForm.name, email: editForm.email, phone: editForm.phone,
        company: editForm.company, city: editForm.city, industry: editForm.industry,
        country: editForm.country, website: editForm.website, vat_number: editForm.vat_number,
        notes: editForm.notes,
        company_id: cid,
      });
      if (r?.data?.success) {
        Alert.alert('Updated', 'Client updated');
        setShowEditClient(false);
        loadAll();
      } else {
        Alert.alert('Failed', r?.data?.message || 'Could not update');
      }
    } catch (e) { Alert.alert('Error', e?.response?.data?.error || e?.message || 'Network error'); }
  };

  // Generic update / delete via /api/data/:tableName/:id (works reliably).
  // Map our entity slugs to the actual underlying table names.
  const TABLE_MAP = {
    projects:       'projects',
    interviews:     'interviews',
    invoices:       'invoices',
    contracts:      'contracts',
    tasks:          'tasks',
    communications: 'crm_communications',
    documents:      'documents',
    feedback:       'customer_feedback',
  };

  const updateItem = async (entity, id, patch) => {
    try {
      const cid   = await getCompanyId();
      const table = TABLE_MAP[entity] || entity;
      const r = await api.put(`/api/data/${table}/${id}`, { ...patch, company_id: cid });
      if (r?.data?.success) loadAll();
      else Alert.alert('Failed', r?.data?.message || 'Update failed');
    } catch (e) { Alert.alert('Error', e?.response?.data?.error || e?.message || 'Network error'); }
  };

  const deleteItem = (entity, id, label = 'item') => {
    Alert.alert('Delete', `Delete this ${label}? This cannot be undone.`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        try {
          const cid   = await getCompanyId();
          const table = TABLE_MAP[entity] || entity;
          const r = await api.delete(`/api/data/${table}/${id}`, { params: { company_id: cid } });
          if (r?.data?.success) loadAll();
          else Alert.alert('Failed', 'Could not delete');
        } catch (e) { Alert.alert('Error', e?.response?.data?.error || e?.message || 'Network error'); }
      }},
    ]);
  };

  const markInvoicePaid = (inv) => {
    Alert.alert('Mark Paid', `Mark invoice ${inv.invoice_number || `#${inv.id}`} as paid?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Mark Paid', onPress: () => updateItem('invoices', inv.id, {
        status: 'paid', paid_at: new Date().toISOString(),
      })},
    ]);
  };

  const toggleTaskDone = (task) => {
    const next = task.status === 'done' ? 'todo' : 'done';
    updateItem('tasks', task.id, { status: next });
  };

  const updateProjectStatus = (proj, status) => {
    updateItem('projects', proj.id, { status });
  };

  const toggleFavorite = async () => {
    const next = !favorite;
    setFavorite(next);
    try { await AsyncStorage.setItem(`client_fav_${clientId}`, next ? '1' : '0'); } catch {}
  };

  // FAB
  const toggleFAB = () => {
    const next = !showFAB;
    Animated.timing(fabAnim, {
      toValue: next ? 1 : 0,
      duration: 200,
      easing: Easing.out(Easing.quad),
      useNativeDriver: true,
    }).start();
    setShowFAB(next);
  };

  // Export
  const handleExport = () => {
    const lines = [
      `📋 CLIENT REPORT: ${client.name || '—'}`,
      `Company: ${client.company || ''}`,
      `Email: ${client.email || ''}   Phone: ${client.phone || ''}`,
      `Health Score: ${Math.round(healthScore)}/100 (${healthLabel})`,
      `Lifetime Value: ${fmt(stats.lifetimeValue)}`,
      '',
      `━━ PROJECTS (${stats.projects}) ━━`,
      ...projects.map(p =>
        `• ${p.project_name} · ${p.status} · ${fmt(p.budget)} · ${p.completion_percentage || 0}%`
      ),
      '',
      `━━ INTERVIEWS (${stats.interviews}) ━━`,
      ...interviews.slice(0, 10).map(i =>
        `• ${i.title} · ${fmtDate(i.scheduled_date)} ${fmtTime(i.scheduled_time)} · ${i.status}`
      ),
      '',
      `━━ CONTRACTS (${stats.contracts}) ━━`,
      ...contracts.map(c =>
        `• ${c.contract_name || c.contract_number} · ${fmt(c.contract_value)} · ${c.status}`
      ),
      '',
      `━━ INVOICES (${stats.invoices}) ━━`,
      `Paid: ${fmt(stats.paidTotal)} · Unpaid: ${fmt(stats.unpaidTotal)} · Overdue: ${fmt(stats.overdueTotal)}`,
    ];
    Share.share({ message: lines.join('\n'), title: `${client.name} — Client Report` });
  };

  // Quick contact actions
  const quickCall = () => client.phone && Linking.openURL(`tel:${client.phone}`);
  const quickEmail = () => client.email && Linking.openURL(`mailto:${client.email}`);
  const quickMessage = () => client.phone && Linking.openURL(`sms:${client.phone}`);

  // ════════════════════════════════════════════════════════════════════════
  // FILTER / SORT / SEARCH HELPERS
  // ════════════════════════════════════════════════════════════════════════
  const applyFilters = (arr, dateField = 'created_at') => {
    let out = arr || [];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      out = out.filter(item => JSON.stringify(item).toLowerCase().includes(q));
    }
    if (filterStatus !== 'all') {
      out = out.filter(item => item.status === filterStatus);
    }
    out = [...out].sort((a, b) => {
      const ta = new Date(a[dateField] || a.scheduled_date || a.start_date || 0).getTime();
      const tb = new Date(b[dateField] || b.scheduled_date || b.start_date || 0).getTime();
      if (sortBy === 'date_asc')   return ta - tb;
      if (sortBy === 'date_desc')  return tb - ta;
      if (sortBy === 'value_desc') return (parseFloat(b.budget || b.amount || b.contract_value) || 0)
                                       - (parseFloat(a.budget || a.amount || a.contract_value) || 0);
      if (sortBy === 'value_asc')  return (parseFloat(a.budget || a.amount || a.contract_value) || 0)
                                       - (parseFloat(b.budget || b.amount || b.contract_value) || 0);
      if (sortBy === 'name_asc') {
        const na = (a.project_name || a.title || a.invoice_number || a.contract_name || a.subject || '').toLowerCase();
        const nb = (b.project_name || b.title || b.invoice_number || b.contract_name || b.subject || '').toLowerCase();
        return na.localeCompare(nb);
      }
      return 0;
    });
    return out;
  };

  // ════════════════════════════════════════════════════════════════════════
  // RENDERS
  // ════════════════════════════════════════════════════════════════════════
  const TABS = [
    { k: 'overview',   l: 'Overview',  i: 'home',               n: 0 },
    { k: 'projects',   l: 'Projects',  i: 'briefcase',          n: stats.projects },
    { k: 'schedule',   l: 'Schedule',  i: 'calendar',           n: stats.upcomingInt + stats.openTasks },
    { k: 'finance',    l: 'Finance',   i: 'cash',               n: stats.invoices + stats.contracts },
    { k: 'messages',   l: 'Messages',  i: 'email',              n: stats.comms },
    { k: 'files',      l: 'Files',     i: 'file',               n: stats.documents },
  ];

  // ── Header ───────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <LinearGradient colors={[PRIMARY, PRIMARY_2]} style={st.header}>
      <View style={st.headerRow}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={st.hBtn}>
          <Ionicons name="arrow-back" size={22} color="#FFF" />
        </TouchableOpacity>
        <View style={[st.avatar, { backgroundColor: 'rgba(255,255,255,0.2)', marginLeft: 8 }]}>
          <Text style={{ color: '#FFF', fontWeight: '800', fontSize: 16 }}>
            {(client.name || '?').substring(0, 2).toUpperCase()}
          </Text>
        </View>
        <View style={{ flex: 1, marginLeft: 10 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Text style={st.hTitle} numberOfLines={1}>{client.name || 'Client'}</Text>
            <StarToggle active={favorite} onPress={toggleFavorite} />
          </View>
          <Text style={{ color: '#93C5FD', fontSize: 11 }} numberOfLines={1}>
            {client.company ? client.company + ' · ' : ''}
            {client.industry || ''}{client.city ? ' · ' + client.city : ''}
          </Text>
        </View>
        <TouchableOpacity style={st.hBtn} onPress={() => { setEditForm(client); setShowEditClient(true); }}>
          <MaterialCommunityIcons name="pencil" size={18} color="#FFF" />
        </TouchableOpacity>
        <TouchableOpacity style={[st.hBtn, { marginLeft: 4 }]} onPress={handleExport}>
          <MaterialCommunityIcons name="export-variant" size={18} color="#FFF" />
        </TouchableOpacity>
      </View>

      {/* Quick contact chips */}
      <View style={{ flexDirection: 'row', marginTop: 10, gap: 6, paddingLeft: 44, flexWrap: 'wrap' }}>
        {client.email && (
          <TouchableOpacity style={st.contactChip} onPress={quickEmail}>
            <MaterialCommunityIcons name="email" size={12} color="#93C5FD" />
            <Text style={st.contactTxt} numberOfLines={1}>{client.email}</Text>
          </TouchableOpacity>
        )}
        {client.phone && (
          <TouchableOpacity style={st.contactChip} onPress={quickCall}>
            <MaterialCommunityIcons name="phone" size={12} color="#93C5FD" />
            <Text style={st.contactTxt}>{client.phone}</Text>
          </TouchableOpacity>
        )}
        {client.website && (
          <TouchableOpacity style={st.contactChip} onPress={() => Linking.openURL(
            client.website.startsWith('http') ? client.website : `https://${client.website}`
          )}>
            <MaterialCommunityIcons name="web" size={12} color="#93C5FD" />
            <Text style={st.contactTxt} numberOfLines={1}>{client.website}</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Quick action row */}
      <View style={st.quickActions}>
        <TouchableOpacity style={st.qaBtn} onPress={quickCall} disabled={!client.phone}>
          <View style={[st.qaIcon, { backgroundColor: client.phone ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.05)' }]}>
            <Ionicons name="call" size={16} color="#FFF" />
          </View>
          <Text style={st.qaLbl}>Call</Text>
        </TouchableOpacity>
        <TouchableOpacity style={st.qaBtn} onPress={quickEmail} disabled={!client.email}>
          <View style={[st.qaIcon, { backgroundColor: client.email ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.05)' }]}>
            <Ionicons name="mail" size={16} color="#FFF" />
          </View>
          <Text style={st.qaLbl}>Email</Text>
        </TouchableOpacity>
        <TouchableOpacity style={st.qaBtn} onPress={quickMessage} disabled={!client.phone}>
          <View style={[st.qaIcon, { backgroundColor: client.phone ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.05)' }]}>
            <Ionicons name="chatbubble" size={16} color="#FFF" />
          </View>
          <Text style={st.qaLbl}>Text</Text>
        </TouchableOpacity>
        <TouchableOpacity style={st.qaBtn} onPress={() => setShowAddInterview(true)}>
          <View style={[st.qaIcon, { backgroundColor: 'rgba(255,255,255,0.18)' }]}>
            <MaterialCommunityIcons name="calendar-plus" size={16} color="#FFF" />
          </View>
          <Text style={st.qaLbl}>Meet</Text>
        </TouchableOpacity>
        <TouchableOpacity style={st.qaBtn} onPress={() => setShowAddComm(true)}>
          <View style={[st.qaIcon, { backgroundColor: 'rgba(255,255,255,0.18)' }]}>
            <MaterialCommunityIcons name="note-plus" size={16} color="#FFF" />
          </View>
          <Text style={st.qaLbl}>Log</Text>
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  // ── KPI bar ──────────────────────────────────────────────────────────────
  const renderKPIs = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.kpiBar}
      contentContainerStyle={{ paddingHorizontal: 14 }}>
      {[
        { l: 'Projects',   v: String(stats.projects),       c: INDIGO,   i: 'briefcase' },
        { l: 'Active',     v: String(stats.activeProjects), c: SUCCESS,  i: 'check-circle' },
        { l: 'LTV',        v: fmt(stats.lifetimeValue),     c: PRIMARY,  i: 'cash-multiple' },
        { l: 'Unpaid',     v: fmt(stats.unpaidTotal),       c: stats.overdueCount > 0 ? DANGER : WARNING, i: 'receipt' },
        { l: 'Meetings',   v: String(stats.upcomingInt),    c: WARNING,  i: 'calendar-clock' },
        { l: 'Contracts',  v: String(stats.activeContracts),c: INFO,     i: 'file-document' },
        { l: 'Tasks',      v: String(stats.openTasks),      c: NEUTRAL,  i: 'checkbox-marked' },
        { l: 'Messages',   v: String(stats.comms),          c: PURPLE,   i: 'email' },
      ].map((k, i) => (
        <View key={i} style={st.kpiItem}>
          <MaterialCommunityIcons name={k.i} size={14} color={k.c} />
          <Text style={[st.kpiVal, { color: k.c }]} numberOfLines={1}>{k.v}</Text>
          <Text style={st.kpiLbl}>{k.l}</Text>
        </View>
      ))}
    </ScrollView>
  );

  // ── Tabs ─────────────────────────────────────────────────────────────────
  const renderTabs = () => (
    <View style={st.tabsRow}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 8 }}>
        {TABS.map(t => (
          <TouchableOpacity key={t.k}
            style={[st.tab, activeTab === t.k && st.activeTab]}
            onPress={() => { setActiveTab(t.k); setSearchQuery(''); setFilterStatus('all'); }}>
            <MaterialCommunityIcons name={t.i} size={16}
              color={activeTab === t.k ? PRIMARY : '#94A3B8'} />
            <Text style={[st.tabTxt, activeTab === t.k && { color: PRIMARY, fontWeight: '700' }]}>
              {t.l}
            </Text>
            {t.n > 0 && (
              <View style={st.tabBadge}>
                <Text style={st.tabBadgeTxt}>{t.n}</Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  // ── Search/sort bar within tab ──────────────────────────────────────────
  const renderSearchBar = (statusOptions = []) => (
    <View style={{ paddingHorizontal: 16, paddingTop: 12, paddingBottom: 4 }}>
      <View style={st.searchRow}>
        <MaterialCommunityIcons name="magnify" size={18} color="#94A3B8" />
        <TextInput
          style={st.searchInput}
          placeholder="Search..."
          placeholderTextColor="#94A3B8"
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons name="close-circle" size={16} color="#94A3B8" />
          </TouchableOpacity>
        )}
        <View style={{ width: 1, height: 18, backgroundColor: BORDER, marginHorizontal: 8 }} />
        <TouchableOpacity onPress={() => {
          const orders = ['date_desc', 'date_asc', 'value_desc', 'value_asc', 'name_asc'];
          const idx = orders.indexOf(sortBy);
          setSortBy(orders[(idx + 1) % orders.length]);
        }}>
          <MaterialCommunityIcons name="sort" size={18} color={PRIMARY} />
        </TouchableOpacity>
      </View>

      {statusOptions.length > 0 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ gap: 6, paddingVertical: 8 }}>
          {['all', ...statusOptions].map(s => (
            <TouchableOpacity key={s}
              style={[st.filterChip, filterStatus === s && st.filterChipActive]}
              onPress={() => setFilterStatus(s)}>
              <Text style={[
                { fontSize: 11, color: NEUTRAL, fontWeight: '600', textTransform: 'capitalize' },
                filterStatus === s && { color: '#FFF' },
              ]}>{s.replace(/_/g, ' ')}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}
    </View>
  );

  // ── Overview ─────────────────────────────────────────────────────────────
  const renderOverview = () => (
    <View style={{ padding: 16 }}>
      {/* Health + LTV card */}
      <View style={[st.card, { padding: 16 }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <HealthRing score={healthScore} />
          <View style={{ flex: 1, marginLeft: 16 }}>
            <Text style={{ fontSize: 11, color: NEUTRAL, fontWeight: '700' }}>RELATIONSHIP</Text>
            <Text style={{ fontSize: 18, fontWeight: '800', color: '#0F172A', marginTop: 2 }}>
              {healthLabel}
            </Text>
            <Text style={{ fontSize: 12, color: NEUTRAL, marginTop: 4, lineHeight: 17 }}>
              Based on comms frequency, payment timeliness, project activity{stats.avgFeedback > 0 ? `, and ${stats.avgFeedback.toFixed(1)}★ feedback` : ''}.
            </Text>
            <View style={{ flexDirection: 'row', marginTop: 10, gap: 10 }}>
              <View>
                <Text style={{ fontSize: 10, color: NEUTRAL, fontWeight: '600' }}>LIFETIME VALUE</Text>
                <Text style={{ fontSize: 16, fontWeight: '800', color: SUCCESS }}>
                  {fmt(stats.lifetimeValue)}
                </Text>
              </View>
              {stats.overdueCount > 0 && (
                <View>
                  <Text style={{ fontSize: 10, color: NEUTRAL, fontWeight: '600' }}>OVERDUE</Text>
                  <Text style={{ fontSize: 16, fontWeight: '800', color: DANGER }}>
                    {fmt(stats.overdueTotal)}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </View>

      {/* Renewal alert */}
      {stats.renewals > 0 && (
        <TouchableOpacity style={[st.alertCard, { borderLeftColor: WARNING }]}
          onPress={() => setActiveTab('finance')}>
          <MaterialCommunityIcons name="clock-alert-outline" size={20} color={WARNING} />
          <Text style={st.alertText}>
            {stats.renewals} contract{stats.renewals === 1 ? '' : 's'} expiring within 60 days
          </Text>
          <MaterialCommunityIcons name="chevron-right" size={20} color={WARNING} />
        </TouchableOpacity>
      )}
      {stats.overdueCount > 0 && (
        <TouchableOpacity style={[st.alertCard, { borderLeftColor: DANGER }]}
          onPress={() => setActiveTab('finance')}>
          <MaterialCommunityIcons name="alert-circle-outline" size={20} color={DANGER} />
          <Text style={st.alertText}>
            {stats.overdueCount} overdue invoice{stats.overdueCount === 1 ? '' : 's'} ({fmt(stats.overdueTotal)})
          </Text>
          <MaterialCommunityIcons name="chevron-right" size={20} color={DANGER} />
        </TouchableOpacity>
      )}

      {/* Client info */}
      <View style={st.card}>
        <View style={st.cardHead}>
          <Text style={st.secTitle}>Client Information</Text>
          <TouchableOpacity style={st.editBtn} onPress={() => { setEditForm(client); setShowEditClient(true); }}>
            <MaterialCommunityIcons name="pencil" size={14} color={PRIMARY} />
            <Text style={st.editBtnTxt}>Edit</Text>
          </TouchableOpacity>
        </View>
        {[
          { l: 'Name',     v: client.name },
          { l: 'Company',  v: client.company },
          { l: 'Email',    v: client.email },
          { l: 'Phone',    v: client.phone },
          { l: 'Website',  v: client.website },
          { l: 'Industry', v: client.industry },
          { l: 'City',     v: client.city },
          { l: 'Country',  v: client.country },
          { l: 'Client #', v: client.client_number },
          { l: 'Status',   v: client.status },
          { l: 'VAT',      v: client.vat_number },
        ].filter(r => r.v).map((r, i) => (
          <View key={i} style={st.row}>
            <Text style={st.rowLbl}>{r.l}</Text>
            <Text style={st.rowVal} numberOfLines={2}>{r.v}</Text>
          </View>
        ))}
      </View>

      {/* Summary cards */}
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 8 }}>
        <View style={[st.summaryCard, { flex: 1 }]}>
          <MaterialCommunityIcons name="briefcase" size={20} color={INDIGO} />
          <Text style={[st.summaryVal, { color: INDIGO }]}>{stats.projects}</Text>
          <Text style={st.summaryLbl}>Projects</Text>
          <Text style={{ fontSize: 10, color: '#94A3B8' }}>{fmt(stats.totalBudget)}</Text>
        </View>
        <View style={[st.summaryCard, { flex: 1 }]}>
          <MaterialCommunityIcons name="calendar-clock" size={20} color={WARNING} />
          <Text style={[st.summaryVal, { color: WARNING }]}>{stats.upcomingInt}</Text>
          <Text style={st.summaryLbl}>Upcoming</Text>
          <Text style={{ fontSize: 10, color: '#94A3B8' }}>interviews</Text>
        </View>
        <View style={[st.summaryCard, { flex: 1 }]}>
          <MaterialCommunityIcons name="receipt" size={20} color={SUCCESS} />
          <Text style={[st.summaryVal, { color: SUCCESS }]}>{fmt(stats.paidTotal)}</Text>
          <Text style={st.summaryLbl}>Paid</Text>
          <Text style={{ fontSize: 10, color: '#94A3B8' }}>{stats.paidCount} invoices</Text>
        </View>
      </View>

      {/* Active projects */}
      {projects.filter(p => p.status === 'active').length > 0 && (
        <View style={st.card}>
          <View style={st.cardHead}>
            <Text style={st.secTitle}>Active Projects</Text>
            <TouchableOpacity onPress={() => setActiveTab('projects')}>
              <Text style={{ fontSize: 12, color: PRIMARY, fontWeight: '700' }}>See all →</Text>
            </TouchableOpacity>
          </View>
          {projects.filter(p => p.status === 'active').slice(0, 3).map((p, i) => (
            <TouchableOpacity key={p.id || i} style={{ marginBottom: 10 }}
              onPress={() => setDetailItem({ type: 'project', data: p })}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: 14, fontWeight: '600', color: '#1E293B', flex: 1 }} numberOfLines={1}>
                  {p.project_name}
                </Text>
                <Text style={{ fontSize: 12, color: SUCCESS, fontWeight: '700' }}>
                  {p.completion_percentage || 0}%
                </Text>
              </View>
              <Text style={{ fontSize: 11, color: '#94A3B8' }}>
                {p.priority} · {fmt(p.budget)}
              </Text>
              <View style={{ height: 4, backgroundColor: BORDER, borderRadius: 2, marginTop: 4 }}>
                <View style={{
                  width: `${p.completion_percentage || 0}%`,
                  height: '100%', backgroundColor: PRIMARY, borderRadius: 2,
                }} />
              </View>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* Open tasks */}
      {tasks.filter(t => t.status !== 'done').length > 0 && (
        <View style={st.card}>
          <View style={st.cardHead}>
            <Text style={st.secTitle}>Open Tasks ({stats.openTasks})</Text>
            <TouchableOpacity onPress={() => setShowAddTask(true)}>
              <Ionicons name="add-circle" size={20} color={PRIMARY} />
            </TouchableOpacity>
          </View>
          {tasks.filter(t => t.status !== 'done').slice(0, 5).map((t, i) => (
            <TouchableOpacity key={t.id || i}
              style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8,
                borderBottomWidth: 1, borderBottomColor: '#F1F5F9' }}
              onPress={() => toggleTaskDone(t)}>
              <View style={{
                width: 18, height: 18, borderRadius: 4, borderWidth: 1.5,
                borderColor: t.priority === 'high' ? DANGER : PRIMARY,
                marginRight: 10,
              }} />
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 13, fontWeight: '600', color: '#1E293B' }} numberOfLines={1}>
                  {t.title}
                </Text>
                {t.due_date && (
                  <Text style={{ fontSize: 11, color: NEUTRAL }}>
                    Due {fmtDate(t.due_date)}
                  </Text>
                )}
              </View>
              <StatusBadge status={t.priority || 'medium'} small />
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* Recent activity timeline */}
      {activityTimeline.length > 0 && (
        <View style={st.card}>
          <View style={st.cardHead}>
            <Text style={st.secTitle}>Recent Activity</Text>
          </View>
          <View style={{ paddingLeft: 4 }}>
            {activityTimeline.slice(0, 8).map((item, i) => (
              <TimelineItem key={i}
                icon={item.icon}
                color={item.color}
                title={item.title}
                subtitle={item.subtitle}
                time={relativeTime(item.time)}
                onPress={() => setDetailItem({ type: item.type, data: item.data })}
              />
            ))}
          </View>
        </View>
      )}

      {/* Feedback */}
      {feedback.length > 0 && (
        <View style={st.card}>
          <View style={st.cardHead}>
            <Text style={st.secTitle}>Feedback ({feedback.length})</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              <MaterialCommunityIcons name="star" size={14} color="#FBBF24" />
              <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A' }}>
                {stats.avgFeedback.toFixed(1)}
              </Text>
            </View>
          </View>
          {feedback.slice(0, 3).map((f, i) => (
            <View key={f.id || i} style={{ paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <View style={{ flexDirection: 'row', gap: 2 }}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <MaterialCommunityIcons key={n}
                      name={n <= (f.rating || 0) ? 'star' : 'star-outline'}
                      size={12} color="#FBBF24" />
                  ))}
                </View>
                <Text style={{ fontSize: 11, color: NEUTRAL }}>{fmtDate(f.created_at)}</Text>
              </View>
              {(f.comment || f.feedback) && (
                <Text style={{ fontSize: 12, color: '#475569', marginTop: 4, lineHeight: 17 }}>
                  {f.comment || f.feedback}
                </Text>
              )}
            </View>
          ))}
        </View>
      )}

      {/* Notes */}
      {client.notes && (
        <View style={st.card}>
          <Text style={st.secTitle}>Notes</Text>
          <Text style={{ fontSize: 14, color: '#475569', lineHeight: 20 }}>{client.notes}</Text>
        </View>
      )}
    </View>
  );

  // ── Projects ─────────────────────────────────────────────────────────────
  const renderProjects = () => {
    const filtered = applyFilters(projects);
    return (
      <View>
        {renderSearchBar(['active', 'completed', 'cancelled', 'pending'])}
        <View style={{ padding: 16, paddingTop: 4 }}>
          <View style={st.cardHead}>
            <Text style={st.secTitle}>{filtered.length} of {projects.length}</Text>
            <TouchableOpacity style={[st.editBtn, { backgroundColor: PRIMARY }]}
              onPress={() => setShowAddProject(true)}>
              <Ionicons name="add" size={16} color="#FFF" />
              <Text style={[st.editBtnTxt, { color: '#FFF' }]}>Add</Text>
            </TouchableOpacity>
          </View>

          {filtered.length === 0 ? (
            <EmptyState icon="briefcase" title="No projects" action="Create Project"
              onAction={() => setShowAddProject(true)} />
          ) : (
            filtered.map((p) => {
              const s = getSt(p.status);
              return (
                <TouchableOpacity key={p.id} style={st.card}
                  onPress={() => setDetailItem({ type: 'project', data: p })}
                  onLongPress={() => Alert.alert(p.project_name || 'Project', 'Actions', [
                    { text: 'Mark Active',    onPress: () => updateProjectStatus(p, 'active') },
                    { text: 'Mark Completed', onPress: () => updateProjectStatus(p, 'completed') },
                    { text: 'Cancel Project', onPress: () => updateProjectStatus(p, 'cancelled') },
                    { text: 'Delete',  style: 'destructive', onPress: () => deleteItem('projects', p.id, 'project') },
                    { text: 'Close',   style: 'cancel' },
                  ])}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 15, fontWeight: '700', color: '#1E293B' }} numberOfLines={1}>
                        {p.project_name}
                      </Text>
                      <Text style={{ fontSize: 12, color: '#94A3B8' }}>
                        {p.priority || 'medium'} · {fmtDate(p.start_date)} – {fmtDate(p.end_date)}
                      </Text>
                      {p.description && (
                        <Text style={{ fontSize: 12, color: '#64748B', marginTop: 2 }} numberOfLines={2}>
                          {p.description}
                        </Text>
                      )}
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                      <StatusBadge status={p.status} />
                      <Text style={{ fontSize: 13, fontWeight: '700', color: PRIMARY, marginTop: 4 }}>
                        {fmt(p.budget)}
                      </Text>
                    </View>
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ fontSize: 12, color: SUCCESS, fontWeight: '600' }}>
                      {p.completion_percentage || 0}% complete
                    </Text>
                    {p.revenue > 0 && (
                      <Text style={{ fontSize: 12, color: '#64748B' }}>
                        Revenue: {fmt(p.revenue)}
                      </Text>
                    )}
                  </View>
                  <View style={{ height: 5, backgroundColor: BORDER, borderRadius: 3, marginTop: 6 }}>
                    <View style={{
                      width: `${p.completion_percentage || 0}%`,
                      height: '100%', backgroundColor: PRIMARY, borderRadius: 3,
                    }} />
                  </View>
                </TouchableOpacity>
              );
            })
          )}
        </View>
      </View>
    );
  };

  // ── Schedule (interviews + tasks merged) ────────────────────────────────
  const renderSchedule = () => {
    const allEvents = [
      ...interviews.map(i => ({ kind: 'interview', date: i.scheduled_date, time: i.scheduled_time, item: i })),
      ...tasks.filter(t => t.due_date).map(t => ({ kind: 'task', date: t.due_date, time: '', item: t })),
    ].sort((a, b) => {
      const da = new Date(a.date || 0).getTime();
      const db = new Date(b.date || 0).getTime();
      return da - db;
    });

    const upcoming = allEvents.filter(e => new Date(e.date) >= new Date());
    const past = allEvents.filter(e => new Date(e.date) < new Date());

    const renderEvent = (e, i) => {
      const d = new Date(e.date);
      const isInt = e.kind === 'interview';
      const s = getSt(e.item.status);
      return (
        <TouchableOpacity key={`${e.kind}-${e.item.id}-${i}`} style={st.card}
          onPress={() => setDetailItem({ type: e.kind, data: e.item })}
          onLongPress={() => Alert.alert(e.item.title, 'Actions', [
            ...(isInt ? [
              { text: 'Mark Completed', onPress: () => updateItem('interviews', e.item.id, { status: 'completed' }) },
              { text: 'Cancel', onPress: () => updateItem('interviews', e.item.id, { status: 'cancelled' }) },
            ] : [
              { text: e.item.status === 'done' ? 'Mark Open' : 'Mark Done', onPress: () => toggleTaskDone(e.item) },
            ]),
            { text: 'Delete', style: 'destructive',
              onPress: () => deleteItem(isInt ? 'interviews' : 'tasks', e.item.id, e.kind) },
            { text: 'Close', style: 'cancel' },
          ])}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <View style={{
              width: 50, height: 50, borderRadius: 12,
              backgroundColor: isInt ? '#EFF6FF' : '#FEF3C7',
              justifyContent: 'center', alignItems: 'center', marginRight: 12,
            }}>
              <Text style={{ fontSize: 18, fontWeight: '800', color: isInt ? PRIMARY : WARNING }}>
                {d.getDate()}
              </Text>
              <Text style={{ fontSize: 10, fontWeight: '600', color: isInt ? PRIMARY_2 : WARNING }}>
                {d.toLocaleDateString('en-GB', { month: 'short' })}
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <MaterialCommunityIcons
                  name={isInt ? 'calendar-clock' : 'checkbox-marked-circle-outline'}
                  size={14} color={isInt ? PRIMARY : WARNING} />
                <Text style={{ fontSize: 14, fontWeight: '600', color: '#1E293B', flex: 1 }} numberOfLines={1}>
                  {e.item.title}
                </Text>
              </View>
              <Text style={{ fontSize: 12, color: '#94A3B8' }}>
                {isInt ? (e.item.interview_type || 'client') : (e.item.priority || 'medium')}
                {e.time ? ` · ${fmtTime(e.time)}` : ''}
              </Text>
              {isInt && e.item.client_representative && (
                <Text style={{ fontSize: 11, color: '#64748B' }}>Rep: {e.item.client_representative}</Text>
              )}
            </View>
            <StatusBadge status={e.item.status} />
          </View>
        </TouchableOpacity>
      );
    };

    return (
      <View style={{ padding: 16 }}>
        <View style={st.cardHead}>
          <Text style={st.secTitle}>Schedule</Text>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <TouchableOpacity style={[st.editBtn, { backgroundColor: PRIMARY }]}
              onPress={() => setShowAddInterview(true)}>
              <MaterialCommunityIcons name="calendar-plus" size={14} color="#FFF" />
              <Text style={[st.editBtnTxt, { color: '#FFF' }]}>Meeting</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[st.editBtn, { backgroundColor: WARNING }]}
              onPress={() => setShowAddTask(true)}>
              <MaterialCommunityIcons name="checkbox-marked-circle-plus-outline" size={14} color="#FFF" />
              <Text style={[st.editBtnTxt, { color: '#FFF' }]}>Task</Text>
            </TouchableOpacity>
          </View>
        </View>

        {allEvents.length === 0 ? (
          <EmptyState icon="calendar-blank" title="Nothing scheduled"
            action="Schedule Meeting" onAction={() => setShowAddInterview(true)} />
        ) : (
          <>
            {upcoming.length > 0 && (
              <>
                <Text style={st.subSecTitle}>Upcoming ({upcoming.length})</Text>
                {upcoming.map(renderEvent)}
              </>
            )}
            {past.length > 0 && (
              <>
                <Text style={st.subSecTitle}>Past ({past.length})</Text>
                {past.slice(0, 20).map(renderEvent)}
              </>
            )}
          </>
        )}
      </View>
    );
  };

  // ── Finance (contracts + invoices) ──────────────────────────────────────
  const renderFinance = () => {
    const monthlyRev = (() => {
      const out = new Array(6).fill(0);
      const now = new Date();
      invoices.filter(i => i.status === 'paid').forEach(i => {
        const t = new Date(i.paid_at || i.created_at || 0);
        const monthsAgo = (now.getFullYear() - t.getFullYear()) * 12 + (now.getMonth() - t.getMonth());
        if (monthsAgo >= 0 && monthsAgo < 6) {
          out[5 - monthsAgo] += parseFloat(i.amount || i.total) || 0;
        }
      });
      return out;
    })();

    return (
      <View style={{ padding: 16 }}>
        {/* Revenue summary */}
        <View style={[st.card, { padding: 16 }]}>
          <Text style={st.secTitle}>Revenue Snapshot</Text>
          <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 8 }}>
            <View>
              <Text style={{ fontSize: 11, color: NEUTRAL, fontWeight: '700' }}>LIFETIME VALUE</Text>
              <Text style={{ fontSize: 26, fontWeight: '800', color: SUCCESS }}>
                {fmt(stats.lifetimeValue)}
              </Text>
            </View>
            <Sparkline data={monthlyRev} color={SUCCESS} height={36} width={130} />
          </View>
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 14 }}>
            <View style={{ flex: 1, padding: 10, backgroundColor: '#F0FDF4', borderRadius: 10 }}>
              <Text style={{ fontSize: 10, color: SUCCESS, fontWeight: '700' }}>PAID</Text>
              <Text style={{ fontSize: 15, fontWeight: '800', color: SUCCESS }}>{fmt(stats.paidTotal)}</Text>
              <Text style={{ fontSize: 10, color: NEUTRAL }}>{stats.paidCount} invoice{stats.paidCount === 1 ? '' : 's'}</Text>
            </View>
            <View style={{ flex: 1, padding: 10, backgroundColor: '#FEF3C7', borderRadius: 10 }}>
              <Text style={{ fontSize: 10, color: WARNING, fontWeight: '700' }}>UNPAID</Text>
              <Text style={{ fontSize: 15, fontWeight: '800', color: WARNING }}>{fmt(stats.unpaidTotal)}</Text>
              <Text style={{ fontSize: 10, color: NEUTRAL }}>{stats.unpaidCount} invoice{stats.unpaidCount === 1 ? '' : 's'}</Text>
            </View>
            {stats.overdueCount > 0 && (
              <View style={{ flex: 1, padding: 10, backgroundColor: '#FEE2E2', borderRadius: 10 }}>
                <Text style={{ fontSize: 10, color: DANGER, fontWeight: '700' }}>OVERDUE</Text>
                <Text style={{ fontSize: 15, fontWeight: '800', color: DANGER }}>{fmt(stats.overdueTotal)}</Text>
                <Text style={{ fontSize: 10, color: NEUTRAL }}>{stats.overdueCount} invoice{stats.overdueCount === 1 ? '' : 's'}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Invoices */}
        <View style={st.cardHead}>
          <Text style={st.secTitle}>Invoices ({invoices.length})</Text>
          <TouchableOpacity style={[st.editBtn, { backgroundColor: PRIMARY }]}
            onPress={() => setShowAddInvoice(true)}>
            <Ionicons name="add" size={16} color="#FFF" />
            <Text style={[st.editBtnTxt, { color: '#FFF' }]}>Add</Text>
          </TouchableOpacity>
        </View>
        {invoices.length === 0 ? (
          <EmptyState icon="receipt-outline" title="No invoices"
            action="Create Invoice" onAction={() => setShowAddInvoice(true)} />
        ) : (
          invoices.map((inv) => {
            const isOverdue = inv.status !== 'paid' && inv.due_date && new Date(inv.due_date) < new Date();
            const displayStatus = isOverdue ? 'overdue' : (inv.status || 'pending');
            return (
              <TouchableOpacity key={inv.id} style={st.card}
                onPress={() => setDetailItem({ type: 'invoice', data: inv })}
                onLongPress={() => Alert.alert(`Invoice ${inv.invoice_number || `#${inv.id}`}`, 'Actions', [
                  ...(inv.status !== 'paid' ? [{ text: 'Mark Paid', onPress: () => markInvoicePaid(inv) }] : []),
                  { text: 'Delete', style: 'destructive', onPress: () => deleteItem('invoices', inv.id, 'invoice') },
                  { text: 'Close', style: 'cancel' },
                ])}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 14, fontWeight: '700', color: '#1E293B' }} numberOfLines={1}>
                      {inv.invoice_number || `Invoice #${inv.id}`}
                    </Text>
                    {inv.description && (
                      <Text style={{ fontSize: 12, color: '#94A3B8' }} numberOfLines={1}>
                        {inv.description}
                      </Text>
                    )}
                    <Text style={{ fontSize: 11, color: NEUTRAL, marginTop: 2 }}>
                      {inv.due_date ? `Due ${fmtDate(inv.due_date)}` : 'No due date'}
                      {inv.paid_at ? ` · Paid ${fmtDate(inv.paid_at)}` : ''}
                    </Text>
                  </View>
                  <View style={{ alignItems: 'flex-end' }}>
                    <Text style={{ fontSize: 16, fontWeight: '800', color: isOverdue ? DANGER : PRIMARY }}>
                      {fmt(inv.amount || inv.total)}
                    </Text>
                    <StatusBadge status={displayStatus} />
                  </View>
                </View>
                {inv.status !== 'paid' && (
                  <TouchableOpacity style={st.markPaidBtn} onPress={() => markInvoicePaid(inv)}>
                    <MaterialCommunityIcons name="check-circle" size={14} color={SUCCESS} />
                    <Text style={{ fontSize: 12, color: SUCCESS, fontWeight: '700', marginLeft: 4 }}>
                      Mark Paid
                    </Text>
                  </TouchableOpacity>
                )}
              </TouchableOpacity>
            );
          })
        )}

        {/* Contracts */}
        <View style={[st.cardHead, { marginTop: 16 }]}>
          <Text style={st.secTitle}>Contracts ({contracts.length})</Text>
        </View>
        {contracts.length === 0 ? (
          <EmptyState icon="file-document-outline" title="No contracts" />
        ) : (
          contracts.map((c) => {
            const daysToExpiry = c.end_date ? daysBetween(new Date(), c.end_date) : null;
            const expiringSoon = daysToExpiry !== null && daysToExpiry > 0 && daysToExpiry <= 60;
            return (
              <TouchableOpacity key={c.id} style={st.card}
                onPress={() => setDetailItem({ type: 'contract', data: c })}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 14, fontWeight: '700', color: '#1E293B' }} numberOfLines={1}>
                      {c.contract_name || c.contract_number}
                    </Text>
                    <Text style={{ fontSize: 12, color: '#94A3B8' }}>
                      {c.contract_type || 'contract'} · {c.contract_number || `#${c.id}`}
                    </Text>
                  </View>
                  <View style={{ alignItems: 'flex-end' }}>
                    <Text style={{ fontSize: 15, fontWeight: '800', color: PRIMARY }}>
                      {fmt(c.contract_value)}
                    </Text>
                    <StatusBadge status={c.status} />
                  </View>
                </View>
                <Text style={{ fontSize: 12, color: '#64748B' }}>
                  {fmtDate(c.start_date)} – {fmtDate(c.end_date)}
                </Text>
                {expiringSoon && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 6,
                    padding: 6, backgroundColor: '#FEF3C7', borderRadius: 6 }}>
                    <MaterialCommunityIcons name="clock-alert" size={12} color={WARNING} />
                    <Text style={{ fontSize: 11, color: WARNING, marginLeft: 4, fontWeight: '700' }}>
                      Expires in {daysToExpiry} day{daysToExpiry === 1 ? '' : 's'}
                    </Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })
        )}
      </View>
    );
  };

  // ── Messages ─────────────────────────────────────────────────────────────
  const renderMessages = () => {
    const filtered = applyFilters(communications, 'sent_at');
    return (
      <View>
        {renderSearchBar([])}
        <View style={{ padding: 16, paddingTop: 4 }}>
          <View style={st.cardHead}>
            <Text style={st.secTitle}>{filtered.length} of {communications.length}</Text>
            <TouchableOpacity style={[st.editBtn, { backgroundColor: PRIMARY }]}
              onPress={() => setShowAddComm(true)}>
              <Ionicons name="add" size={16} color="#FFF" />
              <Text style={[st.editBtnTxt, { color: '#FFF' }]}>Log</Text>
            </TouchableOpacity>
          </View>

          {filtered.length === 0 ? (
            <EmptyState icon="email-outline" title="No messages"
              action="Log Communication" onAction={() => setShowAddComm(true)} />
          ) : (
            filtered.map((cm) => {
              const isOut = cm.direction === 'outbound';
              const typeIcons = {
                email: isOut ? 'email-arrow-right' : 'email-arrow-left',
                phone: 'phone',
                meeting: 'account-group',
                video: 'video',
              };
              return (
                <TouchableOpacity key={cm.id} style={st.card}
                  onPress={() => setDetailItem({ type: 'comm', data: cm })}
                  onLongPress={() => Alert.alert('Message', 'Actions', [
                    { text: 'Delete', style: 'destructive', onPress: () => deleteItem('communications', cm.id, 'message') },
                    { text: 'Close', style: 'cancel' },
                  ])}>
                  <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                    <View style={{
                      width: 36, height: 36, borderRadius: 10,
                      backgroundColor: isOut ? '#EFF6FF' : '#FEF3C7',
                      justifyContent: 'center', alignItems: 'center', marginRight: 10,
                    }}>
                      <MaterialCommunityIcons
                        name={typeIcons[cm.communication_type] || 'email'}
                        size={18}
                        color={isOut ? PRIMARY : WARNING} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 14, fontWeight: '600', color: '#1E293B' }} numberOfLines={1}>
                        {cm.subject}
                      </Text>
                      <Text style={{ fontSize: 11, color: '#94A3B8' }}>
                        {cm.communication_type || 'email'} · {isOut ? 'Sent' : 'Received'} · {relativeTime(cm.sent_at || cm.created_at)}
                      </Text>
                      {cm.content && (
                        <Text style={{ fontSize: 12, color: '#64748B', marginTop: 4 }} numberOfLines={2}>
                          {cm.content}
                        </Text>
                      )}
                    </View>
                    <StatusBadge status={cm.status} small />
                  </View>
                </TouchableOpacity>
              );
            })
          )}
        </View>
      </View>
    );
  };

  // ── Files ───────────────────────────────────────────────────────────────
  const renderFiles = () => (
    <View style={{ padding: 16 }}>
      <View style={st.cardHead}>
        <Text style={st.secTitle}>Documents ({documents.length})</Text>
      </View>
      {documents.length === 0 ? (
        <EmptyState icon="file-outline" title="No documents attached to this client" />
      ) : (
        documents.map((d) => {
          const ext = String(d.filename || d.name || '').split('.').pop().toLowerCase();
          const iconMap = {
            pdf: 'file-pdf-box', doc: 'file-word-box', docx: 'file-word-box',
            xls: 'file-excel-box', xlsx: 'file-excel-box',
            png: 'file-image', jpg: 'file-image', jpeg: 'file-image',
            zip: 'folder-zip',
          };
          const icon = iconMap[ext] || 'file-document';
          const colorMap = {
            pdf: DANGER, doc: PRIMARY, docx: PRIMARY,
            xls: SUCCESS, xlsx: SUCCESS,
            png: INDIGO, jpg: INDIGO, jpeg: INDIGO,
          };
          const c = colorMap[ext] || NEUTRAL;
          return (
            <TouchableOpacity key={d.id} style={st.card}
              onPress={() => d.url && Linking.openURL(d.url).catch(() => Alert.alert('Open Failed', 'Could not open the file'))}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <MaterialCommunityIcons name={icon} size={36} color={c} />
                <View style={{ flex: 1, marginLeft: 10 }}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: '#1E293B' }} numberOfLines={1}>
                    {d.filename || d.name || `Document #${d.id}`}
                  </Text>
                  <Text style={{ fontSize: 11, color: NEUTRAL }}>
                    {d.size ? `${(d.size / 1024).toFixed(1)} KB · ` : ''}
                    {fmtDate(d.created_at || d.uploaded_at)}
                  </Text>
                </View>
                <MaterialCommunityIcons name="download" size={20} color={PRIMARY} />
              </View>
            </TouchableOpacity>
          );
        })
      )}
    </View>
  );

  // Tab router
  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'projects': return renderProjects();
      case 'schedule': return renderSchedule();
      case 'finance':  return renderFinance();
      case 'messages': return renderMessages();
      case 'files':    return renderFiles();
      default:         return renderOverview();
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // MODALS
  // ════════════════════════════════════════════════════════════════════════
  const FormModal = ({ visible, onClose, title, children, onSubmit, submitLabel = 'Save' }) => (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
        <View style={st.modalH}>
          <TouchableOpacity onPress={onClose}>
            <Ionicons name="close" size={24} color={NEUTRAL} />
          </TouchableOpacity>
          <Text style={st.modalTitle}>{title}</Text>
          <View style={{ width: 24 }} />
        </View>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={{ flex: 1 }}>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 40 }}
            keyboardShouldPersistTaps="handled">
            {children}
            <TouchableOpacity style={st.submitBtn} onPress={onSubmit}>
              <MaterialCommunityIcons name="check" size={18} color="#FFF" />
              <Text style={st.submitBtnTxt}>{submitLabel}</Text>
            </TouchableOpacity>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  const renderAddProjectModal = () => (
    <FormModal
      visible={showAddProject}
      onClose={() => setShowAddProject(false)}
      title="New Project"
      submitLabel="Create Project"
      onSubmit={handleAddProject}>
      <Text style={st.formLbl}>Project Name *</Text>
      <TextInput style={st.formInput} value={projForm.project_name}
        onChangeText={t => setProjForm(p => ({ ...p, project_name: t }))}
        placeholder="e.g. Website Redesign" placeholderTextColor="#94A3B8" />
      <Text style={st.formLbl}>Description</Text>
      <TextInput style={[st.formInput, { height: 70, textAlignVertical: 'top' }]}
        value={projForm.description}
        onChangeText={t => setProjForm(p => ({ ...p, description: t }))}
        placeholder="What does the project entail?" placeholderTextColor="#94A3B8"
        multiline />
      <Text style={st.formLbl}>Budget ({currency})</Text>
      <TextInput style={st.formInput} value={projForm.budget}
        onChangeText={t => setProjForm(p => ({ ...p, budget: t }))}
        placeholder="0" placeholderTextColor="#94A3B8" keyboardType="numeric" />
      <Text style={st.formLbl}>Priority</Text>
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 4 }}>
        {['low', 'medium', 'high', 'urgent'].map(pr => (
          <TouchableOpacity key={pr}
            style={[st.chip, projForm.priority === pr && st.chipActive]}
            onPress={() => setProjForm(p => ({ ...p, priority: pr }))}>
            <Text style={[st.chipTxt, projForm.priority === pr && { color: '#FFF' }]}>{pr}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <Text style={st.formLbl}>Start Date</Text>
      <TextInput style={st.formInput} value={projForm.start_date}
        onChangeText={t => setProjForm(p => ({ ...p, start_date: t }))}
        placeholder="YYYY-MM-DD" placeholderTextColor="#94A3B8" />
      <Text style={st.formLbl}>End Date</Text>
      <TextInput style={st.formInput} value={projForm.end_date}
        onChangeText={t => setProjForm(p => ({ ...p, end_date: t }))}
        placeholder="YYYY-MM-DD" placeholderTextColor="#94A3B8" />
    </FormModal>
  );

  const renderAddInterviewModal = () => (
    <FormModal
      visible={showAddInterview}
      onClose={() => setShowAddInterview(false)}
      title="Schedule Meeting"
      submitLabel="Schedule"
      onSubmit={handleAddInterview}>
      <Text style={st.formLbl}>Title *</Text>
      <TextInput style={st.formInput} value={intForm.title}
        onChangeText={t => setIntForm(p => ({ ...p, title: t }))}
        placeholder="Meeting title" placeholderTextColor="#94A3B8" />
      <Text style={st.formLbl}>Type</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
        {['client', 'technical', 'kickoff', 'review', 'final'].map(t => (
          <TouchableOpacity key={t}
            style={[st.chip, intForm.interview_type === t && st.chipActive]}
            onPress={() => setIntForm(p => ({ ...p, interview_type: t }))}>
            <Text style={[st.chipTxt, intForm.interview_type === t && { color: '#FFF' }]}>{t}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <Text style={st.formLbl}>Date</Text>
      <TextInput style={st.formInput} value={intForm.scheduled_date}
        onChangeText={t => setIntForm(p => ({ ...p, scheduled_date: t }))}
        placeholder="YYYY-MM-DD" placeholderTextColor="#94A3B8" />
      <Text style={st.formLbl}>Time</Text>
      <TextInput style={st.formInput} value={intForm.scheduled_time}
        onChangeText={t => setIntForm(p => ({ ...p, scheduled_time: t }))}
        placeholder="HH:MM (24h)" placeholderTextColor="#94A3B8" />
      <Text style={st.formLbl}>Client Representative</Text>
      <TextInput style={st.formInput} value={intForm.client_representative}
        onChangeText={t => setIntForm(p => ({ ...p, client_representative: t }))}
        placeholder="Name" placeholderTextColor="#94A3B8" />
      <Text style={st.formLbl}>Notes</Text>
      <TextInput style={[st.formInput, { height: 70, textAlignVertical: 'top' }]}
        value={intForm.notes}
        onChangeText={t => setIntForm(p => ({ ...p, notes: t }))}
        placeholder="Agenda, prep notes…" placeholderTextColor="#94A3B8"
        multiline />
    </FormModal>
  );

  const renderAddCommModal = () => (
    <FormModal
      visible={showAddComm}
      onClose={() => setShowAddComm(false)}
      title="Log Communication"
      submitLabel="Log Communication"
      onSubmit={handleAddComm}>
      <Text style={st.formLbl}>Type</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
        {['email', 'phone', 'meeting', 'video', 'message'].map(t => (
          <TouchableOpacity key={t}
            style={[st.chip, commForm.communication_type === t && st.chipActive]}
            onPress={() => setCommForm(p => ({ ...p, communication_type: t }))}>
            <Text style={[st.chipTxt, commForm.communication_type === t && { color: '#FFF' }]}>{t}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <Text style={st.formLbl}>Subject *</Text>
      <TextInput style={st.formInput} value={commForm.subject}
        onChangeText={t => setCommForm(p => ({ ...p, subject: t }))}
        placeholder="What was discussed?" placeholderTextColor="#94A3B8" />
      <Text style={st.formLbl}>Content</Text>
      <TextInput style={[st.formInput, { height: 120, textAlignVertical: 'top' }]}
        value={commForm.content}
        onChangeText={t => setCommForm(p => ({ ...p, content: t }))}
        placeholder="Detailed notes…" placeholderTextColor="#94A3B8"
        multiline />
    </FormModal>
  );

  const renderAddInvoiceModal = () => (
    <FormModal
      visible={showAddInvoice}
      onClose={() => setShowAddInvoice(false)}
      title="New Invoice"
      submitLabel="Create Invoice"
      onSubmit={handleAddInvoice}>
      <Text style={st.formLbl}>Invoice Number *</Text>
      <TextInput style={st.formInput} value={invForm.invoice_number}
        onChangeText={t => setInvForm(p => ({ ...p, invoice_number: t }))}
        placeholder="e.g. INV-2026-001" placeholderTextColor="#94A3B8" />
      <Text style={st.formLbl}>Description</Text>
      <TextInput style={[st.formInput, { height: 70, textAlignVertical: 'top' }]}
        value={invForm.description}
        onChangeText={t => setInvForm(p => ({ ...p, description: t }))}
        placeholder="What is being invoiced?" placeholderTextColor="#94A3B8"
        multiline />
      <Text style={st.formLbl}>Amount ({currency}) *</Text>
      <TextInput style={st.formInput} value={invForm.amount}
        onChangeText={t => setInvForm(p => ({ ...p, amount: t }))}
        placeholder="0" placeholderTextColor="#94A3B8" keyboardType="numeric" />
      <Text style={st.formLbl}>Due Date</Text>
      <TextInput style={st.formInput} value={invForm.due_date}
        onChangeText={t => setInvForm(p => ({ ...p, due_date: t }))}
        placeholder="YYYY-MM-DD" placeholderTextColor="#94A3B8" />
      <Text style={st.formLbl}>Status</Text>
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 4 }}>
        {['pending', 'sent', 'paid'].map(s => (
          <TouchableOpacity key={s}
            style={[st.chip, invForm.status === s && st.chipActive]}
            onPress={() => setInvForm(p => ({ ...p, status: s }))}>
            <Text style={[st.chipTxt, invForm.status === s && { color: '#FFF' }]}>{s}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </FormModal>
  );

  const renderAddTaskModal = () => (
    <FormModal
      visible={showAddTask}
      onClose={() => setShowAddTask(false)}
      title="New Task"
      submitLabel="Add Task"
      onSubmit={handleAddTask}>
      <Text style={st.formLbl}>Title *</Text>
      <TextInput style={st.formInput} value={taskForm.title}
        onChangeText={t => setTaskForm(p => ({ ...p, title: t }))}
        placeholder="What needs doing?" placeholderTextColor="#94A3B8" />
      <Text style={st.formLbl}>Description</Text>
      <TextInput style={[st.formInput, { height: 70, textAlignVertical: 'top' }]}
        value={taskForm.description}
        onChangeText={t => setTaskForm(p => ({ ...p, description: t }))}
        placeholder="Additional details…" placeholderTextColor="#94A3B8"
        multiline />
      <Text style={st.formLbl}>Due Date</Text>
      <TextInput style={st.formInput} value={taskForm.due_date}
        onChangeText={t => setTaskForm(p => ({ ...p, due_date: t }))}
        placeholder="YYYY-MM-DD" placeholderTextColor="#94A3B8" />
      <Text style={st.formLbl}>Priority</Text>
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 4 }}>
        {['low', 'medium', 'high'].map(pr => (
          <TouchableOpacity key={pr}
            style={[st.chip, taskForm.priority === pr && st.chipActive]}
            onPress={() => setTaskForm(p => ({ ...p, priority: pr }))}>
            <Text style={[st.chipTxt, taskForm.priority === pr && { color: '#FFF' }]}>{pr}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <Text style={st.formLbl}>Assignee</Text>
      <TextInput style={st.formInput} value={taskForm.assignee}
        onChangeText={t => setTaskForm(p => ({ ...p, assignee: t }))}
        placeholder="Who's responsible?" placeholderTextColor="#94A3B8" />
    </FormModal>
  );

  const renderEditClientModal = () => (
    <FormModal
      visible={showEditClient}
      onClose={() => setShowEditClient(false)}
      title="Edit Client"
      submitLabel="Save Changes"
      onSubmit={handleUpdateClient}>
      {[
        { k: 'name',       l: 'Name' },
        { k: 'company',    l: 'Company' },
        { k: 'email',      l: 'Email' },
        { k: 'phone',      l: 'Phone' },
        { k: 'website',    l: 'Website' },
        { k: 'industry',   l: 'Industry' },
        { k: 'city',       l: 'City' },
        { k: 'country',    l: 'Country' },
        { k: 'vat_number', l: 'VAT Number' },
      ].map(f => (
        <View key={f.k}>
          <Text style={st.formLbl}>{f.l}</Text>
          <TextInput style={st.formInput}
            value={editForm[f.k] || ''}
            onChangeText={t => setEditForm(p => ({ ...p, [f.k]: t }))}
            placeholder={f.l} placeholderTextColor="#94A3B8" />
        </View>
      ))}
      <Text style={st.formLbl}>Notes</Text>
      <TextInput style={[st.formInput, { height: 90, textAlignVertical: 'top' }]}
        value={editForm.notes || ''}
        onChangeText={t => setEditForm(p => ({ ...p, notes: t }))}
        placeholder="Internal notes…" placeholderTextColor="#94A3B8"
        multiline />
    </FormModal>
  );

  // ── Detail modal (generic — shows full item data) ───────────────────────
  const renderDetailModal = () => {
    if (!detailItem) return null;
    const { type, data } = detailItem;
    const title = data.project_name || data.title || data.invoice_number || data.contract_name
      || data.subject || `${type} detail`;
    const close = () => setDetailItem(null);

    return (
      <Modal visible={!!detailItem} animationType="slide" presentationStyle="pageSheet" onRequestClose={close}>
        <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
          <View style={st.modalH}>
            <TouchableOpacity onPress={close}>
              <Ionicons name="close" size={24} color={NEUTRAL} />
            </TouchableOpacity>
            <Text style={st.modalTitle} numberOfLines={1}>{title}</Text>
            <View style={{ width: 24 }} />
          </View>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
            <View style={[st.card, { padding: 14 }]}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text style={{ fontSize: 11, color: NEUTRAL, fontWeight: '700', textTransform: 'uppercase' }}>
                  {type}
                </Text>
                {data.status && <StatusBadge status={data.status} />}
              </View>
              {Object.entries(data)
                .filter(([k, v]) => v != null && v !== '' && typeof v !== 'object'
                  && !['id', 'company_id', 'client_id'].includes(k))
                .map(([k, v]) => (
                  <View key={k} style={st.row}>
                    <Text style={st.rowLbl}>
                      {k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </Text>
                    <Text style={st.rowVal}>
                      {(k.endsWith('_date') || k === 'created_at' || k === 'sent_at' || k === 'paid_at')
                        ? fmtDate(v)
                        : (k === 'budget' || k === 'amount' || k === 'total' || k === 'contract_value' || k === 'revenue')
                          ? fmt(v)
                          : String(v)}
                    </Text>
                  </View>
                ))}
            </View>

            {/* Edit / delete actions */}
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 16 }}>
              {type === 'invoice' && data.status !== 'paid' && (
                <TouchableOpacity style={[st.submitBtn, { flex: 1, marginTop: 0, backgroundColor: SUCCESS }]}
                  onPress={() => { markInvoicePaid(data); close(); }}>
                  <MaterialCommunityIcons name="check-circle" size={18} color="#FFF" />
                  <Text style={st.submitBtnTxt}>Mark Paid</Text>
                </TouchableOpacity>
              )}
              {type === 'task' && (
                <TouchableOpacity style={[st.submitBtn, { flex: 1, marginTop: 0, backgroundColor: data.status === 'done' ? NEUTRAL : SUCCESS }]}
                  onPress={() => { toggleTaskDone(data); close(); }}>
                  <MaterialCommunityIcons name="check-circle" size={18} color="#FFF" />
                  <Text style={st.submitBtnTxt}>
                    {data.status === 'done' ? 'Mark Open' : 'Mark Done'}
                  </Text>
                </TouchableOpacity>
              )}
              {['project', 'interview', 'invoice', 'task', 'contract'].includes(type) && (
                <TouchableOpacity style={[st.submitBtn, { flex: 1, marginTop: 0, backgroundColor: DANGER }]}
                  onPress={() => {
                    const entityMap = {
                      project: 'projects', interview: 'interviews',
                      invoice: 'invoices', task: 'tasks', contract: 'contracts',
                    };
                    deleteItem(entityMap[type], data.id, type);
                    close();
                  }}>
                  <MaterialCommunityIcons name="delete" size={18} color="#FFF" />
                  <Text style={st.submitBtnTxt}>Delete</Text>
                </TouchableOpacity>
              )}
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  // ── FAB (Floating Action Button with menu) ──────────────────────────────
  const renderFAB = () => (
    <View style={st.fabContainer} pointerEvents="box-none">
      {showFAB && (
        <Animated.View style={[st.fabMenu, {
          opacity: fabAnim,
          transform: [{ scale: fabAnim }, {
            translateY: fabAnim.interpolate({ inputRange: [0, 1], outputRange: [20, 0] }),
          }],
        }]} pointerEvents="auto">
          {[
            { icon: 'briefcase-plus',         label: 'Project',  color: INDIGO,
              onPress: () => { toggleFAB(); setShowAddProject(true); } },
            { icon: 'calendar-plus',          label: 'Meeting',  color: PRIMARY,
              onPress: () => { toggleFAB(); setShowAddInterview(true); } },
            { icon: 'note-plus',              label: 'Message',  color: PURPLE,
              onPress: () => { toggleFAB(); setShowAddComm(true); } },
            { icon: 'receipt',                label: 'Invoice',  color: SUCCESS,
              onPress: () => { toggleFAB(); setShowAddInvoice(true); } },
            { icon: 'checkbox-marked-circle-plus-outline', label: 'Task', color: WARNING,
              onPress: () => { toggleFAB(); setShowAddTask(true); } },
          ].map(item => (
            <TouchableOpacity key={item.label}
              style={st.fabItem}
              onPress={item.onPress}>
              <Text style={st.fabItemLbl}>{item.label}</Text>
              <View style={[st.fabItemBtn, { backgroundColor: item.color }]}>
                <MaterialCommunityIcons name={item.icon} size={20} color="#FFF" />
              </View>
            </TouchableOpacity>
          ))}
        </Animated.View>
      )}
      <TouchableOpacity style={[st.fab, { backgroundColor: showFAB ? DANGER : PRIMARY }]}
        onPress={toggleFAB} activeOpacity={0.8}>
        <MaterialCommunityIcons name={showFAB ? 'close' : 'plus'} size={28} color="#FFF" />
      </TouchableOpacity>
    </View>
  );

  // ════════════════════════════════════════════════════════════════════════
  // MAIN
  // ════════════════════════════════════════════════════════════════════════
  if (isLoading) return (
    <SafeAreaView style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: BG }}>
      <StatusBar barStyle="light-content" />
      <ActivityIndicator size="large" color={PRIMARY} />
      <Text style={{ color: NEUTRAL, marginTop: 12, fontWeight: '500' }}>Loading client data…</Text>
    </SafeAreaView>
  );

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
      <StatusBar barStyle="light-content" backgroundColor={PRIMARY} />
      {renderHeader()}
      {renderKPIs()}
      {loadError && !loadError.isFatal && (
        <TouchableOpacity style={st.errorBanner} onPress={onRefresh}>
          <MaterialCommunityIcons name="alert-circle-outline" size={16} color={WARNING} />
          <Text style={st.errorBannerText} numberOfLines={2}>{loadError.message}</Text>
          <MaterialCommunityIcons name="refresh" size={16} color={WARNING} />
        </TouchableOpacity>
      )}
      {renderTabs()}
      <ScrollView style={{ flex: 1 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[PRIMARY]} />}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled">
        {renderContent()}
        <View style={{ height: 80 }} />
      </ScrollView>
      {renderFAB()}
      {renderAddProjectModal()}
      {renderAddInterviewModal()}
      {renderAddCommModal()}
      {renderAddInvoiceModal()}
      {renderAddTaskModal()}
      {renderEditClientModal()}
      {renderDetailModal()}
    </SafeAreaView>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// STYLES
// ════════════════════════════════════════════════════════════════════════════
const st = StyleSheet.create({
  header: { paddingTop: 8, paddingBottom: 14, paddingHorizontal: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  hBtn: { width: 36, height: 36, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.12)',
    justifyContent: 'center', alignItems: 'center' },
  hTitle: { fontSize: 18, fontWeight: '800', color: '#FFF', flexShrink: 1 },
  avatar: { width: 40, height: 40, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  contactChip: { flexDirection: 'row', alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 4, maxWidth: 180 },
  contactTxt: { fontSize: 11, color: '#93C5FD', marginLeft: 4 },

  quickActions: { flexDirection: 'row', justifyContent: 'space-around', marginTop: 14 },
  qaBtn: { alignItems: 'center', gap: 4 },
  qaIcon: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  qaLbl: { fontSize: 10, color: '#FFF', fontWeight: '600' },

  kpiBar: { flexGrow: 0, backgroundColor: SURFACE, paddingVertical: 10,
    borderBottomWidth: 1, borderBottomColor: BORDER },
  kpiItem: { alignItems: 'center', marginRight: 14, minWidth: 60 },
  kpiVal: { fontSize: 13, fontWeight: '800', marginTop: 2 },
  kpiLbl: { fontSize: 9, color: '#94A3B8', marginTop: 1, fontWeight: '600' },

  tabsRow: { backgroundColor: SURFACE, borderBottomWidth: 1, borderBottomColor: BORDER },
  tab: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 10,
    borderRadius: 14, marginRight: 2 },
  activeTab: { backgroundColor: '#EFF6FF' },
  tabTxt: { fontSize: 12, color: '#94A3B8', marginLeft: 4, fontWeight: '500' },
  tabBadge: { backgroundColor: '#0e4e9620', borderRadius: 8, paddingHorizontal: 5,
    paddingVertical: 1, marginLeft: 4, minWidth: 18, alignItems: 'center' },
  tabBadgeTxt: { fontSize: 10, fontWeight: '800', color: PRIMARY },

  searchRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: SURFACE,
    borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8,
    borderWidth: 1, borderColor: BORDER, gap: 8 },
  searchInput: { flex: 1, fontSize: 14, color: '#0F172A', paddingVertical: 0 },
  filterChip: { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 14,
    backgroundColor: '#F1F5F9', borderWidth: 1, borderColor: BORDER },
  filterChipActive: { backgroundColor: PRIMARY, borderColor: PRIMARY },

  card: { backgroundColor: SURFACE, marginBottom: 8, padding: 14, borderRadius: 12,
    borderWidth: 1, borderColor: BORDER },
  cardHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  secTitle: { fontSize: 16, fontWeight: '800', color: '#0F172A' },
  subSecTitle: { fontSize: 12, fontWeight: '800', color: NEUTRAL,
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, marginTop: 6 },
  editBtn: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 5,
    backgroundColor: '#F1F5F9', borderRadius: 8, gap: 4 },
  editBtnTxt: { fontSize: 13, color: PRIMARY, fontWeight: '600' },
  row: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 7,
    borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  rowLbl: { fontSize: 13, color: NEUTRAL, fontWeight: '500', minWidth: 80 },
  rowVal: { fontSize: 14, fontWeight: '600', color: '#1E293B', flex: 1, textAlign: 'right' },

  summaryCard: { backgroundColor: SURFACE, borderRadius: 12, padding: 12, alignItems: 'center',
    borderWidth: 1, borderColor: BORDER },
  summaryVal: { fontSize: 18, fontWeight: '800', marginTop: 4 },
  summaryLbl: { fontSize: 11, color: NEUTRAL, marginTop: 2, fontWeight: '600' },

  alertCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: SURFACE,
    borderRadius: 10, padding: 12, marginBottom: 8, borderLeftWidth: 3, gap: 10 },
  alertText: { flex: 1, fontSize: 13, color: '#1E293B', fontWeight: '600' },

  errorBanner: { flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#FEF3C7', paddingHorizontal: 14, paddingVertical: 8, gap: 8,
    borderBottomWidth: 1, borderBottomColor: '#FDE68A' },
  errorBannerText: { flex: 1, fontSize: 12, color: '#92400E', fontWeight: '600' },

  // Timeline
  tlRow: { flexDirection: 'row', alignItems: 'flex-start' },
  tlLineCol: { width: 24, alignItems: 'center' },
  tlDot: { width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center',
    marginTop: 1 },
  tlLine: { width: 2, flex: 1, backgroundColor: '#F1F5F9', marginTop: 2 },

  markPaidBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    paddingVertical: 6, marginTop: 8, borderRadius: 8, backgroundColor: '#F0FDF4',
    borderWidth: 1, borderColor: '#D1FAE5' },

  empty: { alignItems: 'center', padding: 36 },
  emptyTxt: { fontSize: 14, color: NEUTRAL, marginTop: 10, fontWeight: '500' },
  emptyBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: PRIMARY,
    paddingHorizontal: 16, paddingVertical: 10, borderRadius: 10, marginTop: 14, gap: 6 },
  emptyBtnTxt: { color: '#FFF', fontWeight: '700', fontSize: 13 },

  chip: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 14,
    backgroundColor: '#F1F5F9', borderWidth: 1, borderColor: BORDER },
  chipActive: { backgroundColor: PRIMARY, borderColor: PRIMARY },
  chipTxt: { fontSize: 12, color: NEUTRAL, fontWeight: '500', textTransform: 'capitalize' },

  modalH: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    padding: 16, borderBottomWidth: 1, borderBottomColor: BORDER, backgroundColor: SURFACE },
  modalTitle: { fontSize: 17, fontWeight: '700', color: '#0F172A', flex: 1, textAlign: 'center' },
  formLbl: { fontSize: 13, fontWeight: '600', color: '#334155', marginBottom: 6, marginTop: 10 },
  formInput: { backgroundColor: SURFACE, borderWidth: 1.5, borderColor: BORDER, borderRadius: 10,
    paddingHorizontal: 14, paddingVertical: 12, fontSize: 15, color: '#0F172A', marginBottom: 4 },
  submitBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: PRIMARY, borderRadius: 12, paddingVertical: 14, marginTop: 16, gap: 6 },
  submitBtnTxt: { color: '#FFF', fontSize: 16, fontWeight: '700' },

  // FAB
  fabContainer: { position: 'absolute', bottom: 20, right: 20, alignItems: 'flex-end' },
  fab: { width: 56, height: 56, borderRadius: 28, alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 6,
    elevation: 8 },
  fabMenu: { alignItems: 'flex-end', marginBottom: 10, gap: 10 },
  fabItem: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  fabItemLbl: { backgroundColor: SURFACE, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8,
    fontSize: 13, fontWeight: '600', color: '#1E293B',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.15, shadowRadius: 3,
    elevation: 3 },
  fabItemBtn: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.2, shadowRadius: 4,
    elevation: 5 },
});
